import { useMemo, useState } from "react";
import { Check, RotateCcw, RefreshCw } from "lucide-react";

/**
 * Flashcard deck
 * - Click card (or press Enter/Space) to flip
 * - "Got it" removes card from the session
 * - "Review again" sends card to the back of the deck
 * - When deck is empty → success state with restart
 */
export default function Flashcards({ cards, lessonId }) {
  const initialDeck = useMemo(
    () => cards.map((c, i) => ({ ...c, _k: `${lessonId}-${i}` })),
    [cards, lessonId]
  );
  const [deck, setDeck] = useState(initialDeck);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [gotCount, setGotCount] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);

  const current = deck[idx];
  const total = initialDeck.length;
  const remaining = deck.length;

  const next = (wasGot) => {
    setFlipped(false);
    if (wasGot) {
      setGotCount((n) => n + 1);
      setDeck((d) => d.filter((_, i) => i !== idx));
      setIdx((i) => (i >= deck.length - 1 ? 0 : i));
    } else {
      setReviewCount((n) => n + 1);
      setDeck((d) => {
        const copy = [...d];
        const [item] = copy.splice(idx, 1);
        copy.push(item);
        return copy;
      });
      setIdx((i) => (i >= deck.length - 1 ? 0 : i));
    }
  };

  const restart = () => {
    setDeck(initialDeck);
    setIdx(0);
    setFlipped(false);
    setGotCount(0);
    setReviewCount(0);
  };

  if (!current) {
    return (
      <section
        className="card rise"
        style={{
          padding: 32,
          textAlign: "center",
          marginTop: 24,
        }}
        data-testid="flashcards-complete"
        aria-live="polite"
      >
        <div
          aria-hidden="true"
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "color-mix(in srgb, var(--mod-2) 18%, transparent)",
            color: "var(--mod-2)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 16,
          }}
        >
          <Check size={22} strokeWidth={2.5} />
        </div>
        <h3 className="t-heading" style={{ color: "var(--text)", margin: "0 0 8px" }}>
          All cards reviewed
        </h3>
        <p className="t-body" style={{ margin: "0 0 24px" }}>
          You went through {total} flashcard{total === 1 ? "" : "s"}. Got it: {gotCount} ·
          Reviewed again: {reviewCount}.
        </p>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={restart}
          data-testid="flashcards-restart-btn"
        >
          <RotateCcw size={16} strokeWidth={1.75} aria-hidden="true" /> Restart deck
        </button>
      </section>
    );
  }

  return (
    <section aria-label="Flashcards" style={{ marginTop: 16 }}>
      {/* meta row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <span
          className="t-meta"
          data-testid="flashcards-progress-label"
        >
          Card {total - remaining + 1} of {total}
        </span>
        <span className="t-meta" style={{ color: "var(--muted)" }}>
          Tap to flip
        </span>
      </div>

      {/* flip card */}
      <div className="flip-scene" style={{ marginBottom: 16 }}>
        <div
          role="button"
          tabIndex={0}
          aria-pressed={flipped}
          aria-label={flipped ? "Showing answer, click to show question" : "Showing question, click to show answer"}
          className={`flip-card rise${flipped ? " is-flipped" : ""}`}
          data-testid="flashcard-flip"
          onClick={() => setFlipped((f) => !f)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setFlipped((f) => !f);
            }
          }}
          style={{ cursor: "pointer" }}
          key={current._k}
        >
          <div className="flip-face front">
            <div>
              <div className="t-meta">Question</div>
              <p
                style={{
                  margin: "12px 0 0",
                  fontSize: 17,
                  lineHeight: 1.55,
                  color: "var(--text)",
                }}
              >
                {current.q}
              </p>
            </div>
            <div className="t-meta" style={{ color: "var(--muted)" }}>
              Click to reveal answer
            </div>
          </div>
          <div className="flip-face back">
            <div>
              <div className="t-meta" style={{ color: "var(--accent)" }}>
                Answer
              </div>
              <p
                style={{
                  margin: "12px 0 0",
                  fontSize: 17,
                  lineHeight: 1.55,
                  color: "var(--text)",
                }}
              >
                {current.a}
              </p>
            </div>
            <div className="t-meta" style={{ color: "var(--muted)" }}>
              Click to flip back
            </div>
          </div>
        </div>
      </div>

      {/* action buttons – appear once flipped to encourage reveal first */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
        }}
      >
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => next(false)}
          data-testid="flashcard-review-btn"
          aria-label="Review this card again later"
        >
          <RefreshCw size={16} strokeWidth={1.75} aria-hidden="true" />
          Review again
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => next(true)}
          data-testid="flashcard-got-it-btn"
          aria-label="I got this card"
        >
          <Check size={16} strokeWidth={2} aria-hidden="true" />
          Got it
        </button>
      </div>
    </section>
  );
}
